import React from 'react'

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  useToast,
} from '@chakra-ui/react'

import {supabase} from '@/api'
import {CommonModalProps} from '@/utils/types'

import useLoadingState from '../use-loading-state'

type Props = CommonModalProps & {
  table?: string
  id?: string | number
  onDelete?: () => Promise<void>
  headerText?: string
  name?: string
  onFailTitle?: string
  onSuccessTitle?: string
}

const DeleteResourceModal = ({
  onClose,
  open,
  table,
  id,
  onComplete,
  onDelete,
  headerText,
  name,
  onFailTitle,
  onSuccessTitle,
}: Props) => {
  const toast = useToast()
  const cancelButtonRef = React.useRef<HTMLButtonElement>(null)

  const handleDelete = React.useCallback(async () => {
    try {
      if (onDelete) {
        await onDelete()
      } else if (!!id && !!table) {
        const {error, count} = await supabase.from(table).delete({count: 'exact'}).match({id})
        if (error) throw error
        if (!count) throw new Error('No rows deleted')
      } else {
        throw new Error('Missing item details')
      }
      toast({
        isClosable: true,
        status: 'success',
        title: onSuccessTitle || 'Usunięto',
      })
      onClose()
      onComplete && onComplete()
    } catch (e) {
      console.error('Failed to delete item', e)
      toast({
        isClosable: true,
        title: onFailTitle || 'Nie udało się usunąć zasobu',
        status: 'error',
      })
    }
  }, [id, onFailTitle, onSuccessTitle, onClose, onComplete, onDelete, table, toast])
  const {loading, handleSubmit} = useLoadingState(handleDelete)

  return (
    <AlertDialog isOpen={open} onClose={onClose} size="lg" leastDestructiveRef={cancelButtonRef}>
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader>{headerText || 'Czy jesteś pewny?'}</AlertDialogHeader>
        <AlertDialogBody>
          Czy na pewno chcesz usunąć {name ? <strong>{name}</strong> : 'zasób'}? Pamiętaj, że zmiany są
          nieodwracalne.
        </AlertDialogBody>
        <AlertDialogFooter>
          <HStack py={2} justifyContent="space-evenly">
            <Button colorScheme="red" isLoading={loading} onClick={handleSubmit}>
              Usuń
            </Button>
            <Button ref={cancelButtonRef} variant="ghost" onClick={onClose} isDisabled={loading}>
              Anuluj
            </Button>
          </HStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteResourceModal
